import React from 'react'
import { TouchableOpacity, ScrollView } from 'react-native'
import { Feather } from '../../components/icon/vectorIcons'
import { StackScreenProps } from '../../navigation/stack/createStackNavigator'

import { ScreenContainerWithItemHeader } from '../../components/layout/ScreenContainer'
import { Flex } from '../../components/FlexBox'
import { H4, Text } from '../../components/common/Text'
import useTranslation from '../../hooks/useTranslation'
import usePreventNavigationOnDirtyState from '../../hooks/usePreventNavigationOnDirtyState'
import { AppStackParamList } from '../../types'
import { TOPIC_FEED_MIN_ITEMS } from './constants'
import useContentFeedDetail from './hooks/useContentFeedDetail'
import FeedContentPanel from './components/FeedContentPanel'
import FeedSettingsModal from './components/FeedSettingsModal'
import SelectContentModal from './components/SelectContentModal'

type Props = StackScreenProps<AppStackParamList, 'ContentFeedDetail'>

export const ContentFeedDetailScreen = ({ route }: Props) => {
  const { t } = useTranslation()
  const id = route.params?.id

  const {
    feed,
    loading,
    error,
    settings,
    updateSettings,
    isDirty,
    changeCount,
    publishBlockedReason,
    settingsChanges,
    saving,
    applySettings,
    publish,
    discard,
    isSettingsOpen,
    openSettings,
    closeSettings,
    boosts,
    boostsLoading,
    boostsError,
    reorderBoosts,
    removeBoost,
    isSelectOpen,
    openSelect,
    closeSelect,
    confirmSelection,
    remainingSlots,
    maxItems
  } = useContentFeedDetail(id)

  // The guard only reads `.dirty`, so the settings form's own dirty flag is
  // enough -- no need to pull formik in for four fields.
  usePreventNavigationOnDirtyState({ dirty: isDirty })

  return (
    <ScreenContainerWithItemHeader
      screenCategory="ContentFeedDetail"
      defaultBack="ContentFeedList"
    >
      <ScrollView>
        <Flex flexDirection="row" alignItems="center" mx={4} my={3}>
          <Flex flex={1}>
            <H4>{feed?.name ?? t('contentCuration:detail:loading')}</H4>
          </Flex>
          {!!feed && (
            <TouchableOpacity
              onPress={openSettings}
              accessibilityLabel={t('contentCuration:settings:title')}
              style={{ padding: 8 }}
            >
              <Feather name="settings" size={22} color="#21201B" />
            </TouchableOpacity>
          )}
        </Flex>

        {loading && (
          <Flex mx={4} my={3}>
            <Text>{t('common:loading')}</Text>
          </Flex>
        )}
        {!!error && (
          <Flex mx={4} my={3}>
            <Text>{t('contentCuration:detail:error')}</Text>
          </Flex>
        )}
        {!loading && !error && !feed && (
          <Flex mx={4} my={3}>
            <Text>{t('contentCuration:detail:notFound')}</Text>
          </Flex>
        )}

        {!!feed && (
          <>
            <FeedContentPanel
              boosts={boosts}
              loading={boostsLoading}
              error={boostsError}
              onReorder={reorderBoosts}
              onRemove={removeBoost}
              onAdd={openSelect}
              maxItems={maxItems}
              changeCount={changeCount}
              publishing={saving}
              publishBlockedReason={publishBlockedReason}
              onPublish={publish}
              onDiscard={discard}
            />
            <SelectContentModal
              open={isSelectOpen}
              close={closeSelect}
              contentFeedId={feed.id}
              feedTopicIds={feed.topics.map(topic => topic.id)}
              currentBoosts={boosts}
              mode="add"
              remainingSlots={remainingSlots}
              maxItems={maxItems}
              minItems={TOPIC_FEED_MIN_ITEMS}
              onConfirm={confirmSelection}
              saving={saving}
            />
            <FeedSettingsModal
              open={isSettingsOpen}
              close={closeSettings}
              settings={settings}
              slug={feed.slug}
              itemCount={boosts.length}
              onChange={updateSettings}
              onApply={applySettings}
              dirty={settingsChanges > 0}
              saving={saving}
            />
          </>
        )}
      </ScrollView>
    </ScreenContainerWithItemHeader>
  )
}

export default ContentFeedDetailScreen
